import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { language, toggleLanguage } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const navItems = [
    { id: 'home', label: language === 'en' ? 'HOME' : 'ACCUEIL', href: '#' },
    { id: 'about', label: language === 'en' ? 'ABOUT' : 'À PROPOS', href: '#about' },
    { id: 'classes', label: language === 'en' ? 'CLASSES' : 'PROGRAMMES', href: '#classes' },
    { id: 'contact', label: language === 'en' ? 'CONTACT' : 'CONTACT', href: '#contact' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
            ? 'bg-gray-900/80 backdrop-blur-md border-b border-gray-800 py-3'
            : 'bg-transparent py-6'
          }`}
      >
        <div className="container mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <svg className="w-8 h-8 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
              <path d="M14.5,11c0.83,0,1.5-0.67,1.5-1.5v-4C16,4.67,15.33,4,14.5,4h-9C4.67,4,4,4.67,4,5.5v4C4,10.33,4.67,11,5.5,11H7v1H5.5 C4.67,12,4,12.67,4,13.5v4C4,18.33,4.67,19,5.5,19h9c0.83,0,1.5-0.67,1.5-1.5v-4c0-0.83-0.67-1.5-1.5-1.5H13v-1H14.5z M11,11H9 v-1h2V11z" />
            </svg>
            <span className="font-heading font-bold text-xl tracking-wide text-white">HASSAN LAFANI</span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={item.href}
                className="text-xs lg:text-sm font-medium text-gray-300 hover:text-blue-400 transition duration-300 font-heading tracking-wide"
              >
                {item.label}
              </a>
            ))}

            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-1 text-white hover:text-blue-400 transition duration-300 font-heading font-bold"
            >
              <span className={language === 'en' ? 'text-blue-500' : 'text-gray-400'}>EN</span>
              <span className="text-gray-600">/</span>
              <span className={language === 'fr' ? 'text-blue-500' : 'text-gray-400'}>FR</span>
            </button>

            <a href="https://www.instagram.com/hassan_lafani?igsh=MTJsMTJjbzdjdTRodg%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="bg-blue-600 hover:bg-blue-700 text-white text-xs lg:text-sm font-bold py-2 px-5 rounded-full transition duration-300 shadow-lg shadow-blue-500/20 font-heading tracking-wide">
              {language === 'en' ? 'BOOK NOW' : 'RÉSERVER'}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-4">
            {/* Mobile Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-1 text-white hover:text-blue-400 transition duration-300 font-heading font-bold text-sm"
            >
              <span className={language === 'en' ? 'text-blue-500' : 'text-gray-400'}>EN</span>
              <span className="text-gray-600">/</span>
              <span className={language === 'fr' ? 'text-blue-500' : 'text-gray-400'}>FR</span>
            </button>

            <button
              className="text-white focus:outline-none"
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Dropdown */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden fixed top-16 inset-x-0 z-40 bg-gray-900/95 backdrop-blur-md border-b border-gray-800 shadow-lg"
          >
            <div className="container mx-auto px-6 flex flex-col space-y-4 py-6">
              {navItems.map((item) => (
                <a
                  key={item.id}
                  href={item.href}
                  className="text-white hover:text-blue-400 transition duration-300 py-2 px-3 hover:bg-white/5 rounded font-heading tracking-wide"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </a>
              ))}
              <a href="https://www.instagram.com/hassan_lafani?igsh=MTJsMTJjbzdjdTRodg%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition duration-300 text-center font-heading tracking-wide mt-2">
                {language === 'en' ? 'BOOK NOW' : 'RÉSERVER'}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
